import {
  LoadingSpinner,
  NavLinkButton,
  Separator,
  useToast,
} from '@ais-ui/ui-core';
import { ROUTES } from '../../routes';
import { PiCaretLeftThin } from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';
import { InputOTPForm, PinFormValues } from './InputOTPForm';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { initEnableMFA, validateEnableMFA } from '@ais-ui/websockets';
import { useAuth, useCredentials } from '@ais-ui/auth';
import QRCode from 'react-qr-code';
import { useNavigate } from 'react-router';

interface MultiFactorPageProps {
  authenticated: boolean;
}

export const MultiFactorPage = ({ authenticated }: MultiFactorPageProps) => {
  const { messages } = useLocalization();
  const { sessions } = useAuth();

  const { toast } = useToast();
  const credentials = useCredentials();
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  const olSession = useMemo(() => sessions?.olSession, [sessions]);

  const { setTwoFactorEnabled } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const initMFA = async () => {
      setStatus('loading');
      try {
        if (!olSession) {
          throw new Error('Failed to initialize MFA, missing session');
        }

        const response = await initEnableMFA(
          olSession,
          credentials?.username ? credentials.username : '',
          credentials?.password ? credentials.password : ''
        );
        setMfaSecret(response.secret);
        setStatus('success');
      } catch (error) {
        console.error('Error initializing MFA:', error);
        setMfaSecret('');
        setStatus('error');
      }
    };

    initMFA();
  }, [olSession, credentials?.username, credentials?.password]);

  const getQRValue = useCallback(() => {
    const issuer = 'GateHouse MDM';
    return `otpauth://totp/${issuer}:${credentials?.username}?secret=${mfaSecret}&issuer=${issuer}`;
  }, [credentials?.username, mfaSecret]);

  const onEnableMFA = useCallback(
    async (data: PinFormValues) => {
      if (!mfaSecret) {
        toast({
          title: 'Error',
          description: 'Failed to enable MFA, missing secret',
          variant: 'destructive',
        });
        return;
      }

      const pin = Number(data.pin);

      try {
        if (!olSession) {
          throw new Error('Failed to initialize MFA, missing session');
        }
        const response = await validateEnableMFA(
          olSession,
          credentials?.username ? credentials.username : '',
          credentials?.password ? credentials.password : '',
          mfaSecret,
          pin
        );

        if (!response.success) {
          toast({
            title: 'Error',
            description: 'Failed to enable MFA, invalid pin',
            variant: 'destructive',
          });
          return;
        }

        toast({
          title: 'Success',
          description: 'MFA has been enabled successfully',
        });
        setTwoFactorEnabled(true);
        if (authenticated) {
          navigate(ROUTES.SETTINGS.MULTIFACTOR.$path());
        } else {
          // Clear password from localStorage after MFA setup completes
          // Keep only username for next login attempt
          const username = credentials?.username || '';
          if (username) {
            localStorage.setItem('credentials', JSON.stringify({ username }));
          }
          navigate(ROUTES.LOGIN.$path());
        }
      } catch (error: Error | any) {
        let description = 'Failed to enable MFA';
        if (error?.message === 'TOPT code is invalid') {
          description = 'Authentication code is invalid';
        }

        toast({
          title: 'Error',
          description: description,
          variant: 'destructive',
        });
        return;
      }
    },
    [
      authenticated,
      credentials?.password,
      credentials?.username,
      mfaSecret,
      navigate,
      olSession,
      setTwoFactorEnabled,
      toast,
    ]
  );

  return (
    <div className="flex justify-center">
      <div className="hidden space-y-6 p-10 pb-16 md:block w-[984px]">
        {authenticated && (
          <div className="w-30">
            <NavLinkButton
              id="settings-view-settings-button"
              to={ROUTES.SETTINGS.$path()}
              className="p-0 m-0"
            >
              <PiCaretLeftThin className="p-0" />
              <div className="pl-2">{messages.settingsGoBackToSettings}</div>
            </NavLinkButton>
          </div>
        )}
        <div className="space-y-0.5">
          <h2 className="text-2xl font-bold tracking-tight">
            {messages.multiFactorSetupAuthenticatorTitle}
          </h2>
          <p className="text-muted-foreground">
            {messages.multiFactorSetupAuthenticatorQrSubtitle}
          </p>
        </div>
        <Separator className="my-6" />
        <div className="flex flex-col space-y-8 lg:flex-row lg:space-x-12 lg:space-y-0 w-full">
          {status === 'loading' && <LoadingSpinner />}
          {status === 'success' && mfaSecret && (
            <QRCode
              value={getQRValue()}
              size={256}
              level="M"
              className="p-4 rounded-lg border"
            />
          )}
          {status === 'error' && (
            <div className="text-red-600 p-4 rounded-lg border border-red-300 bg-red-50">
              <p className="font-medium">Failed to generate QR code</p>
              <p className="text-sm mt-1">
                Please try again or contact support if the issue persists
              </p>
            </div>
          )}
        </div>
        <InputOTPForm onSubmit={onEnableMFA} />
      </div>
    </div>
  );
};
